import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity } from '@models/activity.model';
import { ActivityHeader } from '../ActivityHeader';

import { formatActivityTime } from '@utils/formatDate';
import { Card } from '@components/atoms/Card';
import Alert from '@components/atoms/Alert';
import ProjectGeofenceModal from '@components/organisms/ProjectGeofenceModal';
import { GeofenceCategoryIcon } from '@utils/geofenceCategoryIcons';
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import LaunchIcon from '@mui/icons-material/Launch';
import PinDropOutlinedIcon from '@mui/icons-material/PinDropOutlined';
import { useTranslation } from '@hooks/useTranslation';

export const ActivityGeofence = ({
  activity,
}: {
  activity: Activity;
  handleOpenTaskDetail?: (taskId: string) => void;
  onProjectClick?: (projectId: string) => void;
}) => {
  const tActivityCards = useTranslation('activityCards');
  const [isGeofenceModalOpen, setIsGeofenceModalOpen] = useState(false);

  const DESCRIPTION_ENTER_GEOFENCE = 'User ENTER geofence';

  const description =
    activity.description === DESCRIPTION_ENTER_GEOFENCE ? 'ENTER' : 'EXIT';

  const variant =
    activity.description === DESCRIPTION_ENTER_GEOFENCE ? 'info' : 'default';

  const geofence = activity.punchClock?.geofence ?? null;
  const project = activity.project ?? activity.punchClock?.project ?? null;
  // The modal expects a Project shape with at least lat/long for the map. The
  // resolver loads it through `activity.project` so we just pass it through.
  const modalProject = project as any;

  const rightLabel = (
    <Alert variant={variant} className="w-fit">
      <div className="text-sm space-y-2">
        <div className="flex items-center gap-2 text-base text-neutral-800 font-semibold">
          {description === 'ENTER' ? (
            <>
              <ArrowForwardOutlinedIcon
                fontSize="small"
                className="text-gray-700"
              />
              <ArrowForwardOutlinedIcon
                fontSize="small"
                className="text-gray-700"
              />
              <ArrowForwardOutlinedIcon
                fontSize="small"
                className="text-gray-700"
              />
            </>
          ) : (
            <>
              <ArrowBackOutlinedIcon
                fontSize="small"
                className="text-gray-700"
              />
              <ArrowBackOutlinedIcon
                fontSize="small"
                className="text-gray-700"
              />
              <ArrowBackOutlinedIcon
                fontSize="small"
                className="text-gray-700"
              />
            </>
          )}
          {description && (
            <p className="text-gray-700">
              {description === 'ENTER' ? tActivityCards.geofence.enter : tActivityCards.geofence.exit}
            </p>
          )}
          {(() => {
            const eventTime = activity.punchClock?.time ?? activity.createdAt;
            return eventTime ? (
              <span className="text-gray-600">
                {formatActivityTime(eventTime)}
              </span>
            ) : null;
          })()}
        </div>
      </div>
    </Alert>
  );

  return (
    <Card>
      <ActivityHeader activity={activity} rightLabel={rightLabel} />

      {(project?.name || project?.projectId) && (
        <div className="flex items-start justify-between gap-2 mt-2">
          <div className="flex items-center gap-1 text-sm text-gray-600 min-w-0">
            <LocationOnOutlinedIcon fontSize="small" className="text-gray-500 flex-shrink-0" />
            <div className="flex flex-col min-w-0">
              {project.projectId ? (
                <Link
                  to={`/projects/${project.projectId}`}
                  className="font-medium text-indigo-600 hover:text-indigo-700 inline-flex items-center gap-1 truncate"
                >
                  {project.name}
                  <LaunchIcon sx={{ fontSize: 12 }} className="flex-shrink-0" />
                </Link>
              ) : (
                <span className="font-medium truncate">{project.name}</span>
              )}
              {project.address && (
                <span className="text-xs text-gray-500 truncate">{project.address}</span>
              )}
            </div>
          </div>
          {project?.projectId && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setIsGeofenceModalOpen(true);
              }}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium text-indigo-700 bg-white ring-1 ring-indigo-200 hover:bg-indigo-50 flex-shrink-0"
            >
              <PinDropOutlinedIcon sx={{ fontSize: 14 }} />
              {tActivityCards.geofence.geofenceButton}
            </button>
          )}
        </div>
      )}

      {geofence && (
        <div className="mt-3 flex items-center gap-2 rounded-md bg-gray-50 px-3 py-2">
          <div
            className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0"
            style={{
              backgroundColor: `${geofence.category?.color || geofence.color || '#6366F1'}20`,
              color: geofence.category?.color || geofence.color || '#6366F1',
            }}
          >
            <GeofenceCategoryIcon
              iconKey={geofence.category?.icon}
              sx={{ fontSize: 16 }}
            />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {geofence.name}
            </p>
            {geofence.category?.name && (
              <p className="text-xs text-gray-500 truncate">
                {geofence.category.name}
              </p>
            )}
          </div>
        </div>
      )}

      {project?.projectId && (
        <ProjectGeofenceModal
          isOpen={isGeofenceModalOpen}
          onClose={() => setIsGeofenceModalOpen(false)}
          project={modalProject}
          hideActiveToggle
          initialGeofenceId={geofence?.geofenceId ?? null}
        />
      )}
    </Card>
  );
};
